import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Sparkles } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { can } from '@/lib/permissions';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import CampaignsListPanel from '@/components/CampaignsListPanel';
import CampaignMediaPanel from '@/components/CampaignMediaPanel';

// One page for the whole send workflow, split by channel.
//
// Making the creative and sending it are one job, so they are one page with tabs rather than two
// sibling routes an operator has to know are halves of the same thing. The active tab arrives as a
// prop from the route table (App.tsx) rather than being parsed out of the location, which keeps the
// routes the single source of truth and lets TypeScript check the two literals at the call site.

type Tab = 'whatsapp' | 'media';

// SMS and Email have no backend at all yet. They are listed so the shape of the product is legible,
// disabled so nobody can act on them, and explained on hover so the disabling isn't a dead end.
const COMING_SOON = [
  { value: 'sms', labelKey: 'campaigns.tab_sms', fallback: 'SMS' },
  { value: 'email', labelKey: 'campaigns.tab_email', fallback: 'Email' },
] as const;

interface CampaignsProps {
  tab: Tab;
}

export default function Campaigns({ tab }: CampaignsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId, permissions } = useWorkspace();
  // Creating used to be gated structurally: the button navigated to /create, which App.tsx wraps in
  // SubscriptionGate. Moving creation onto this page removed that protection without anything
  // LOOKING removed - and this page is deliberately not gated itself, because a lapsed workspace
  // must still be able to see what its last campaign did.
  const canCreate = can(permissions, 'campaign:create');
  // One guard for the page: the hook's `nudge` is rendered once, below, and `guard` is handed to
  // whichever panel needs it. Calling the hook again inside a panel would render a second dialog.
  const { guard, nudge } = useSubscriptionGuard('Creating campaigns requires an active subscription.');

  // Neither of these is guarded, and deliberately so: both only navigate, and the page each lands
  // on is wrapped in SubscriptionGate, so a lapsed workspace is stopped at the destination rather
  // than here. Creating a campaign no longer POSTs from this page at all — the setup stepper creates
  // the row when its first step (name & description) is submitted, so an abandoned click leaves
  // nothing behind.
  // The tab is the channel. Only the WhatsApp tab offers this button today; carrying the tab rather
  // than a literal means an SMS tab will create SMS campaigns without touching this line.
  const handleCreateCampaign = () => navigate(`/w/${workspaceId}/campaigns/new?channel=${tab}`);
  const handleCreateMedia = () => navigate(`/w/${workspaceId}/campaigns/media/new`);

  const isMedia = tab === 'media';

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 p-4 md:p-8">
        <div className="container mx-auto max-w-6xl">
          {/* The title stays put across tabs. A heading that changes on tab click makes the tabs
              feel like navigation to a different page — the exact split this page removes. */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-1">{t('campaigns.title', 'Campaigns')}</h1>
              <p className="text-muted-foreground">
                {isMedia
                  ? t('campaigns.media_subtitle', 'Generated copy, photos and videos. Each one can be used by any number of campaigns.')
                  : t('campaigns.subtitle', 'Resume, launch or manage your campaigns.')}
              </p>
            </div>
            {canCreate && (
              isMedia ? (
                <Button onClick={handleCreateMedia}>
                  <Sparkles className="mr-2 h-4 w-4" /> {t('campaigns.media_new', 'New media')}
                </Button>
              ) : (
                <Button onClick={handleCreateCampaign}>
                  <Sparkles className="mr-2 h-4 w-4" /> {t('campaigns.new', 'Create new campaign')}
                </Button>
              )
            )}
          </div>

          <Tabs
            value={tab}
            onValueChange={(v) => navigate(`/w/${workspaceId}/campaigns/${v}`)}
            className="mb-2"
          >
            <TabsList>
              <TabsTrigger value="media">{t('campaigns.tab_media', 'Media')}</TabsTrigger>
              <TabsTrigger value="whatsapp">{t('campaigns.tab_whatsapp', 'WhatsApp')}</TabsTrigger>

              {/* A disabled TabsTrigger carries `pointer-events: none`, so it can never fire the
                  hover that opens a tooltip — the explanation would be silently unreachable. The
                  tooltip therefore hangs off a wrapper that is NOT disabled. The span is focusable
                  so the reason is available by keyboard as well as by mouse; that is an extra tab
                  stop inside the tablist, which is the price of the explanation existing at all.
                  Do not collapse this back onto the trigger. */}
              {COMING_SOON.map((c) => {
                const label = t(c.labelKey, c.fallback);
                return (
                  <Tooltip key={c.value}>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        className="inline-flex cursor-not-allowed rounded-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <TabsTrigger value={c.value} disabled>{label}</TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {t('campaigns.tab_soon', '{{channel}} campaigns are coming soon.', { channel: label })}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TabsList>

            <TabsContent value={tab} className="mt-6">
              {isMedia
                ? <CampaignMediaPanel onCreate={handleCreateMedia} guard={guard} />
                : <CampaignsListPanel onCreate={handleCreateCampaign} guard={guard} />}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {nudge}
    </div>
  );
}
